import { useEffect, useState } from "react";
import api from "@/lib/api";
import Eyebrow from "@/components/Eyebrow";
import { CalendarDays, MapPin, Trophy } from "lucide-react";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function parseDate(d) {
  const [y, m, day] = String(d || "").split("-").map(Number);
  return { y, m, day };
}

function resultOf(f) {
  if (f.score_for > f.score_against) return { key: "W", label: "Win", color: "#1F5C38" };
  if (f.score_for < f.score_against) return { key: "L", label: "Loss", color: "#A62639" };
  return { key: "D", label: "Draw", color: "#D98A18" };
}

function HomeAwayPill({ v }) {
  const home = v === "home";
  return (
    <span className={`text-[10.5px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 rounded-full ${home ? "bg-mango/25 text-palm-deep" : "bg-indigo2/15 text-indigo2"}`}>
      {home ? "Home" : "Away"}
    </span>
  );
}

function FixtureRow({ f }) {
  const { day, m } = parseDate(f.date);
  return (
    <div className="flex items-center gap-4 bg-white border border-ink/10 rounded-[12px] p-4" data-testid={`fixture-row-${f.id}`}>
      <div className="shrink-0 w-14 text-center bg-palm text-white rounded-[10px] py-2">
        <div className="font-display text-xl leading-none">{day}</div>
        <div className="text-[10px] font-bold tracking-[0.14em] mt-0.5">{MONTHS[(m || 1) - 1]}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display text-lg leading-tight">{f.home_away === "home" ? `vs ${f.opponent}` : `at ${f.opponent}`}</span>
          <HomeAwayPill v={f.home_away} />
          {f.status === "postponed" && (
            <span className="text-[10.5px] font-bold uppercase tracking-[0.1em] px-2 py-0.5 rounded-full bg-berry text-white">Postponed</span>
          )}
        </div>
        <div className="text-sm text-ink/60 mt-1 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1"><Trophy size={12} /> {f.competition || "Friendly"}</span>
          {f.venue && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {f.venue}</span>}
          {f.time && <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {f.time}</span>}
        </div>
      </div>
    </div>
  );
}

function ResultRow({ f }) {
  const r = resultOf(f);
  const { day, m, y } = parseDate(f.date);
  return (
    <div className="flex items-center gap-4 bg-white border border-ink/10 rounded-[12px] p-4" data-testid={`result-row-${f.id}`}>
      <span
        className="shrink-0 w-9 h-9 rounded-full text-white font-display text-lg inline-flex items-center justify-center"
        style={{ background: r.color }}
        title={r.label}
      >
        {r.key}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold">
          ESAFC <span className="font-display text-lg mx-1">{f.score_for} – {f.score_against}</span> {f.opponent}
        </div>
        <div className="text-sm text-ink/60 mt-0.5">
          {MONTHS[(m || 1) - 1]} {day}, {y} · {f.home_away === "home" ? "Home" : "Away"}{f.venue ? ` · ${f.venue}` : ""}
        </div>
      </div>
    </div>
  );
}

export default function FixturesSection() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/fixtures").then((r) => setData(r.data)).catch(() => setData({ upcoming: [], results: [] }));
  }, []);

  if (!data) return null;

  return (
    <section className="py-24 bg-paper" data-testid="esafc-fixtures-section">
      <div className="max-w-[1140px] mx-auto px-6">
        <Eyebrow>Matchday</Eyebrow>
        <h2 className="text-[clamp(30px,4vw,44px)] mb-10 font-display">Fixtures &amp; results.</h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <h3 className="text-xs uppercase tracking-[0.16em] font-bold text-palm mb-4">Upcoming fixtures</h3>
            {data.upcoming.length === 0 ? (
              <div className="border border-dashed border-ink/20 rounded-[12px] p-8 text-ink/60 text-sm" data-testid="fixtures-empty">
                New season fixtures will be announced soon — check back.
              </div>
            ) : (
              <div className="space-y-3">
                {data.upcoming.map((f) => <FixtureRow key={f.id} f={f} />)}
              </div>
            )}
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.16em] font-bold text-clay mb-4">Recent results</h3>
            {data.results.length === 0 ? (
              <div className="border border-dashed border-ink/20 rounded-[12px] p-8 text-ink/60 text-sm" data-testid="results-empty">
                No results yet this season.
              </div>
            ) : (
              <div className="space-y-3">
                {data.results.map((f) => <ResultRow key={f.id} f={f} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
